import { API_URL, ApiError } from '../../shared/api/http'

const key = 'spectra-portaria-resident-session'
const clientKey = 'spectra-portaria-resident-client'
const reads = new Map<string, {until:number; value:unknown}>()
const pending = new Map<string, Promise<unknown>>()
let cacheIdentity: string | null = null
let cacheRevision = 0
export function invalidateResidentReads() { reads.clear(); pending.clear(); cacheRevision++ }

export type ResidentProfile = {
  clientId: string; personId: string; unitId: string; name: string;
  clientName: string; unitLabel: string; blockName: string | null; street?:string|null; mustChangePassword?: boolean
}
export type ResidentSession = { accessToken: string; expiresAt: string }

export function savedResidentClient(): string {
  return localStorage.getItem(clientKey) ?? ''
}

export function saveResidentClient(clientId: string) {
  localStorage.setItem(clientKey, clientId)
}

export function residentToken(): string | null {
  return sessionStorage.getItem(key) ?? localStorage.getItem(key)
}

export function saveResidentSession(session: ResidentSession, remember: boolean) {
  invalidateResidentReads()
  sessionStorage.removeItem(key)
  localStorage.removeItem(key)
  ;(remember ? localStorage : sessionStorage).setItem(key, session.accessToken)
}

export function clearResidentSession() {
  invalidateResidentReads(); cacheIdentity = null
  sessionStorage.removeItem(key)
  localStorage.removeItem(key)
}

export async function residentApi<T>(path: string, body?: unknown,
  method: 'GET' | 'POST' | 'PUT' = body === undefined ? 'GET' : 'POST'): Promise<T> {
  const token = residentToken()
  if (token !== cacheIdentity) { invalidateResidentReads(); cacheIdentity = token }
  const safeRead = Boolean(token) && ((method === 'GET' && /^(portal\/people|household-assets|packages|areas|reservations|announcements|documents|tickets\/[^/]+\/photos)$/.test(path)) || (method === 'POST' && /^(tickets\/search|portal\/history|areas\/[^/]+\/calendar)$/.test(path)))
  if (!safeRead && method !== 'GET' && !['first-access'].includes(path)) invalidateResidentReads()
  const cacheKey = `${method}:${path}:${safeRead && body ? JSON.stringify(body) : ''}`
  const cached = safeRead ? reads.get(cacheKey) : undefined
  if (cached && cached.until > Date.now()) return cached.value as T
  if (safeRead && pending.has(cacheKey)) return pending.get(cacheKey) as Promise<T>
  const revision = cacheRevision
  const operation = residentFetch<T>(path, body, method, token).then(value => {
    if (safeRead && revision === cacheRevision) {
      for (const [key,entry] of reads) if (entry.until <= Date.now()) reads.delete(key)
      const oldest=reads.keys().next().value
      if (reads.size>=64 && oldest) reads.delete(oldest)
      reads.set(cacheKey, {until:Date.now()+20000,value})
    }
    return value
  })
  if (safeRead) pending.set(cacheKey, operation)
  try { return await operation }
  finally { if (pending.get(cacheKey) === operation) pending.delete(cacheKey) }
}

async function residentFetch<T>(path:string, body:unknown, method:string, token:string|null):Promise<T> {
  const headers = new Headers()
  if (body !== undefined) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Portaria ${token}`)
  const response = await fetch(`${API_URL}/portaria/residents/${path}`, {
    method, headers, body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'omit', cache: 'no-store',
  })
  const payload = response.status === 204 || !response.headers.get('content-type')?.includes('json')
    ? null : await response.json()
  if (!response.ok) {
    const code = payload && typeof payload === 'object' && 'code' in payload
      ? String(payload.code) : 'generic_error'
    throw new ApiError(code, response.status)
  }
  return payload as T
}
